import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../../lib/supabase';
import { useTranslation } from 'react-i18next';
import { getAuthenticatedHomePath } from '../../../lib/adminAccess';

const configuredAdminEmails = String(
  import.meta.env.VITE_AERO_ADMIN_EMAILS ?? import.meta.env.VITE_PASSPORT_ADMIN_EMAILS ?? '',
);

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isEnglish = i18n.resolvedLanguage?.startsWith('en');
  const passportPath = session?.user
    ? getAuthenticatedHomePath(session.user, configuredAdminEmails)
    : '/app/login?next=%2Fapp%2Fdashboard';
  const stats = [
    {
      value: isEnglish ? 'AI' : 'IA',
      label: t('hero.stats.planning'),
    },
    {
      value: isEnglish ? 'Curation' : 'Curadoria',
      label: t('hero.stats.specialized'),
    },
    {
      value: isEnglish ? 'Support' : 'Suporte',
      label: t('hero.stats.support'),
    },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!supabase) return;

    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession ?? null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return (
    <section
      id="hero"
      className={`relative flex items-center overflow-hidden ${isLanding ? 'min-h-[88svh]' : 'min-h-screen'}`}
    >
      {/* Cinematic background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=ultra-luxury-travel-destination-aerial-view-of-maldives-overwater-villas-at-golden-sunset-turquoise-ocean-crystal-clear-water-private-infinity-pool-tropical-paradise-cinematic-editorial-photography-high-end-resort-dramatic-sky-warm-golden-light-reflections-on-water-breathtaking-panoramic-view&width=1920&height=1080&seq=hero-luxury-bg-01&orientation=landscape"
          alt="Luxury travel destination"
          className="w-full h-full object-cover object-center"
        />
        {/* Deep cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#040C1A]/90 via-[#060E1E]/75 to-[#060E1E]/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#040C1A]/80 via-transparent to-[#040C1A]/40" />
      </div>

      <div className={`relative z-10 mx-auto flex w-full max-w-5xl flex-col items-start px-5 sm:px-8 lg:px-16 ${isLanding ? 'pb-14 pt-28 sm:py-28' : 'py-28 sm:py-36'}`}>

        {/* Top label */}
        <div
          className="mb-7 flex items-center gap-3 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '0ms' }}
        >
          <div className="h-px w-8 bg-[#C9A84C]" />
              <span className="text-xs font-semibold tracking-[0.25em] uppercase text-[#E8D4A0]">
                {isLanding ? 'Aero Destiny Travel' : t('hero.eyebrow')}
              </span>
        </div>

        {/* Headline */}
        <h1
          className="mb-6 text-[2.6rem] font-bold leading-[1.08] sm:text-5xl lg:text-[3.75rem] transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(28px)', transitionDelay: '120ms' }}
        >
          {isLanding ? <span className="block text-white">Aero <span className="text-[#E8D4A0]">Passport</span></span> : <><span className="block text-white tracking-tight">{t('hero.titleLine1')}</span><span className="block text-white tracking-tight">{t('hero.titleLine2')}</span><span className="block text-[#C9A84C] tracking-tight">{t('hero.titleLine3')}</span></>}
        </h1>

        {/* Subheadline */}
        <div
          className="mb-9 border-l-2 border-[#C9A84C]/30 pl-4 transition-all duration-700 sm:pl-5"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '240ms' }}
        >
          {isLanding ? <p className="max-w-xl text-lg font-medium leading-relaxed text-white sm:text-xl">{isEnglish ? 'Your companion before, during and after travel. Plan the next trip, save flights and places, and keep the notes and memories that make it yours.' : 'Seu companheiro antes, durante e depois da viagem. Planeje a próxima, salve voos e lugares e guarde as notas e memórias que a tornam sua.'}</p> : <><p className="text-lg sm:text-xl text-white leading-relaxed font-medium drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]">{t('hero.sub1')}</p><p className="text-lg sm:text-xl text-white leading-relaxed font-medium drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]">{t('hero.sub2')}</p><p className="text-lg sm:text-xl text-white leading-relaxed font-medium drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]">{t('hero.sub3')}</p></>}
        </div>

        {/* Buttons */}
        <div
          className="flex flex-wrap items-center gap-4 mb-5 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '360ms' }}
        >
          <button
            onClick={() => scrollToSection('contact')}
            className="px-8 py-4 bg-[#C9A84C] text-[#060E1E] text-sm font-bold rounded-lg hover:bg-[#D4B55A] transition-all duration-200 shadow-xl shadow-[#C9A84C]/25 hover:shadow-[#C9A84C]/50 hover:scale-[1.03] cursor-pointer whitespace-nowrap tracking-wide"
          >
            {isLanding ? isEnglish ? 'Plan a new trip' : 'Planejar nova viagem' : t('hero.create')}
          </button>

          <Link
            to={passportPath}
            className="flex items-center gap-2.5 rounded-lg border border-white/30 bg-white/10 px-8 py-4 text-sm font-semibold text-white backdrop-blur transition-colors duration-200 hover:bg-white/20"
          >
            {isLanding ? isEnglish ? 'Open Aero Passport' : 'Abrir Aero Passport' : session?.user ? t('hero.dashboard') : t('hero.login')}
            <span aria-hidden="true" className="text-base">→</span>
          </Link>
        </div>

        {!isLanding && <div
          className="mb-14 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '420ms' }}
        >
          <button
            onClick={() => scrollToSection('como-funciona')}
            className="text-white/90 text-sm font-medium hover:text-white transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2.5 group"
          >
            {t('hero.howItWorks')}
            <span className="group-hover:translate-x-1 transition-transform duration-200 text-base">→</span>
          </button>
        </div>}

        {/* Stats row */}
        {!isLanding && <div
          className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-10 pt-8 sm:pt-10 border-t border-white/[0.08] transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '480ms' }}
        >
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1">
              <span className="text-2xl font-bold text-white tracking-tight">{stat.value}</span>
              <span className="text-xs text-white font-medium uppercase tracking-wider drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]">
                {stat.label}
              </span>
            </div>
          ))}
        </div>}
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 z-[2] pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #060E1E)' }} />
    </section>
  );
}
